/**
 * Web Push 离线推送管理
 * - 注册 Service Worker
 * - 获取 VAPID 公钥
 * - 订阅/取消推送
 */
import { api } from './api'

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

let _swRegistration = null

/**
 * 初始化 Web Push 订阅
 * 1. 注册 Service Worker
 * 2. 获取 VAPID 公钥
 * 3. 调用 pushManager.subscribe()
 * 4. 将订阅信息发送到后端
 */
export async function initPushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
  // HTTPS 或 localhost 才支持
  if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') return

  try {
    // 注册 Service Worker
    _swRegistration = await navigator.serviceWorker.register('/sw.js')
    await navigator.serviceWorker.ready

    // 检查是否已有订阅
    let subscription = await _swRegistration.pushManager.getSubscription()
    if (subscription) {
      // 已有订阅，确保后端知道
      await _sendSubscriptionToServer(subscription)
      return
    }

    // 确保通知权限
    if (Notification.permission === 'default') {
      const result = await Notification.requestPermission()
      if (result !== 'granted') return
    } else if (Notification.permission !== 'granted') {
      return
    }

    // 获取 VAPID 公钥
    const vapidData = await api.getVapidPublicKey()
    if (!vapidData || !vapidData.publicKey) {
      console.warn('[push] VAPID 公钥获取失败')
      return
    }

    // 订阅推送
    subscription = await _swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidData.publicKey)
    })

    // 发送到后端
    await _sendSubscriptionToServer(subscription)
    console.log('[push] 推送订阅成功')
  } catch (e) {
    console.warn('[push] 推送订阅失败:', e.message)
  }
}

/**
 * 取消推送订阅
 */
export async function removePushSubscription() {
  if (!_swRegistration) return
  try {
    const subscription = await _swRegistration.pushManager.getSubscription()
    if (subscription) {
      await api.unsubscribePush(subscription.endpoint)
      await subscription.unsubscribe()
      console.log('[push] 推送订阅已取消')
    }
  } catch (e) {
    console.warn('[push] 取消订阅失败:', e.message)
  }
}

/**
 * 按 conversationId 选中会话并跳到消息页。
 * 列表里没有该会话（新会话 / 列表还没加载完）时，先强拉一次会话列表再选，避免静默失败。
 */
async function selectConversationById(store, conversationId) {
  if (!conversationId || !store) return
  function pick() {
    return store.state.sessionItems.find(function (s) { return s.conversationId === conversationId })
  }
  let session = pick()
  if (!session) {
    try { await store.dispatch('fetchConversations', { force: true }) } catch (e) { /* ignore */ }
    session = pick()
  }
  if (!session) return
  // 先确保跳到 /messages 路由再 setActiveSession;否则 push 失败会导致"未读被清但页面没切换"
  const router = window.__vueRouter
  const isOnMessages = () => router && router.currentRoute && router.currentRoute.name === 'messages'
  if (!isOnMessages()) {
    let pushed = false
    if (router) {
      try {
        await router.push({ name: 'messages' })
        pushed = isOnMessages()
      } catch (e) {
        // NavigationDuplicated/Redirected 视作成功;真失败下面有 hash 兜底
        const name = (e && (e.name || (e.constructor && e.constructor.name))) || ''
        if (/Duplicated|Redirect/i.test(name)) pushed = isOnMessages()
        else console.warn('[push] router.push 失败,启用 hash 兜底:', e && e.message)
      }
    }
    if (!pushed) {
      // 兜底:直接改 hash,触发浏览器 hashchange → Vue Router hash 模式自动响应
      try { window.location.hash = '#/messages' } catch (_) { /* ignore */ }
      // 等 hashchange 把路由切过去,再继续(最多等 300ms)
      const t0 = Date.now()
      while (!isOnMessages() && Date.now() - t0 < 300) {
        await new Promise(function (r) { setTimeout(r, 30) })
      }
      if (!isOnMessages()) {
        console.warn('[push] 路由仍未切到 /messages,放弃 setActiveSession(防止误清未读)')
        return
      }
    }
  }
  store.dispatch('setActiveSession', session.key)
}

/**
 * 监听 Service Worker 消息（warm：ARP 已打开，点通知时 SW postMessage 过来）
 */
export function listenPushMessages(store) {
  if (!('serviceWorker' in navigator)) return
  navigator.serviceWorker.addEventListener('message', function (event) {
    if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
      selectConversationById(store, event.data.conversationId)
    }
  })
}

/**
 * cold：ARP 由通知冷启动，URL 带 ?conversationId=（hash 路由 /#/messages?conversationId=xxx）。
 * 解析出来选中对应会话，处理完把该 query 抹掉，刷新不再重复触发。
 */
export function handleNotificationDeepLink(store) {
  if (!store) return
  let convId = ''
  try {
    const hash = window.location.hash || ''
    const qi = hash.indexOf('?')
    if (qi !== -1) convId = new URLSearchParams(hash.slice(qi + 1)).get('conversationId') || ''
    if (!convId) convId = new URLSearchParams(window.location.search).get('conversationId') || ''
  } catch (e) { /* ignore */ }
  if (!convId) return
  selectConversationById(store, convId).then(function () {
    try {
      const router = window.__vueRouter
      const cur = router && router.currentRoute
      if (cur && cur.query && cur.query.conversationId) {
        const q = Object.assign({}, cur.query)
        delete q.conversationId
        router.replace({ name: cur.name || 'messages', query: q }).catch(function () {})
      }
    } catch (e) { /* ignore */ }
  })
}

async function _sendSubscriptionToServer(subscription) {
  const json = subscription.toJSON()
  const keys = json.keys || {}
  await api.subscribePush({
    endpoint: json.endpoint,
    authKey: keys.auth || '',
    p256dhKey: keys.p256dh || '',
    deviceId: _getDeviceId()
  })
}

function _getDeviceId() {
  let id = localStorage.getItem('push_device_id')
  if (!id) {
    id = 'web-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36)
    localStorage.setItem('push_device_id', id)
  }
  return id
}
